import { useMemo, useState } from "react";
import { useLocale } from "../contexts/LocaleContext";

interface FaqEntry {
  q: string;
  a: string;
}

interface FaqCategory {
  title: string;
  entries: FaqEntry[];
  email?: string;
}

function FaqItem({ q, a }: FaqEntry) {
  const [open, setOpen] = useState(false);

  return (
    <article className="faq-item" aria-expanded={open}>
      <button className="faq-q" type="button" onClick={() => setOpen((v) => !v)}>
        <span>{q}</span>
        <svg className="chev" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </button>
      <div className="faq-a">
        <p>{a}</p>
      </div>
    </article>
  );
}

export function HelpPage() {
  const { t } = useLocale();
  const [term, setTerm] = useState("");

  const categories = useMemo(
    (): FaqCategory[] => [
      {
        title: t("help.cat1Title"),
        entries: [
          { q: t("help.cat1q1"), a: t("help.cat1a1") },
          { q: t("help.cat1q2"), a: t("help.cat1a2") },
          { q: t("help.cat1q3"), a: t("help.cat1a3") },
          { q: t("help.cat1q4"), a: t("help.cat1a4") },
          { q: t("help.cat1q5"), a: t("help.cat1a5") },
        ],
        email: "ask@color-the-world.com",
      },
      {
        title: t("help.cat2Title"),
        entries: [
          { q: t("help.cat2q1"), a: t("help.cat2a1") },
          { q: t("help.cat2q2"), a: t("help.cat2a2") },
          { q: t("help.cat2q3"), a: t("help.cat2a3") },
          { q: t("help.cat2q4"), a: t("help.cat2a4") },
          { q: t("help.cat2q5"), a: t("help.cat2a5") },
        ],
        email: "ask@color-the-world.com",
      },
      {
        title: t("help.cat3Title"),
        entries: [
          { q: t("help.cat3q1"), a: t("help.cat3a1") },
          { q: t("help.cat3q2"), a: t("help.cat3a2") },
          { q: t("help.cat3q3"), a: t("help.cat3a3") },
          { q: t("help.cat3q4"), a: t("help.cat3a4") },
          { q: t("help.cat3q5"), a: t("help.cat3a5") },
          { q: t("help.cat3q6"), a: t("help.cat3a6") },
          { q: t("help.cat3q7"), a: t("help.cat3a7") },
          { q: t("help.cat3q8"), a: t("help.cat3a8") },
          { q: t("help.cat3q9"), a: t("help.cat3a9") },
          { q: t("help.cat3q10"), a: t("help.cat3a10") },
          { q: t("help.cat3q11"), a: t("help.cat3a11") },
          { q: t("help.cat3q12"), a: t("help.cat3a12") },
          { q: t("help.cat3q13"), a: t("help.cat3a13") },
        ],
        email: "ask@color-the-world.com",
      },
      {
        title: t("help.cat4Title"),
        entries: [
          { q: t("help.cat4q1"), a: t("help.cat4a1") },
          { q: t("help.cat4q2"), a: t("help.cat4a2") },
          { q: t("help.cat4q3"), a: t("help.cat4a3") },
          { q: t("help.cat4q4"), a: t("help.cat4a4") },
          { q: t("help.cat4q5"), a: t("help.cat4a5") },
        ],
        email: "ask@color-the-world.com",
      },
      {
        title: t("help.cat5Title"),
        entries: [{ q: t("help.cat5q1"), a: t("help.cat5a1") }],
        email: "ask@color-the-world.com",
      },
      {
        title: t("help.cat6Title"),
        entries: [{ q: t("help.cat6q1"), a: t("help.cat6a1") }],
        email: "ask@color-the-world.com",
      },
      {
        title: t("help.cat7Title"),
        entries: [
          { q: t("help.cat7q1"), a: t("help.cat7a1") },
          { q: t("help.cat7q2"), a: t("help.cat7a2") },
          { q: t("help.cat7q3"), a: t("help.cat7a3") },
          { q: t("help.cat7q4"), a: t("help.cat7a4") },
          { q: t("help.cat7q5"), a: t("help.cat7a5") },
          { q: t("help.cat7q6"), a: t("help.cat7a6") },
          { q: t("help.cat7q7"), a: t("help.cat7a7") },
        ],
        email: "ask@color-the-world.com",
      },
      {
        title: t("help.cat8Title"),
        entries: [{ q: t("help.cat8q1"), a: t("help.cat8a1") }],
        email: "ask@color-the-world.com",
      },
    ],
    [t],
  );

  const visible = useMemo(() => {
    const search = term.trim().toLowerCase();
    if (!search) return categories;

    return categories
      .map((cat) => ({
        ...cat,
        entries: cat.entries.filter(
          (e) =>
            e.q.toLowerCase().includes(search) ||
            e.a.toLowerCase().includes(search),
        ),
      }))
      .filter((cat) => cat.entries.length > 0);
  }, [term, categories]);

  const anyVisible = visible.length > 0;

  return (
    <section className="wrapper faq-page" aria-label="FAQ">
      <div className="faq-hero">
        <h1>{t("help.title")}</h1>
        <p>{t("help.subtitle")}</p>
      </div>

      <div className="faq-search" role="search">
        <label className="visually-hidden" htmlFor="faqSearchInput">
          {t("help.searchLabel")}
        </label>
        <input
          id="faqSearchInput"
          type="text"
          placeholder={t("help.searchPlaceholder")}
          autoComplete="off"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />
        <button id="faqSearchBtn" type="button" aria-label={t("help.search")}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M10 2a8 8 0 105.293 14.293l4.707 4.707 1.414-1.414-4.707-4.707A8 8 0 0010 2zm0 2a6 6 0 110 12 6 6 0 010-12z" />
          </svg>
        </button>
      </div>

      {!anyVisible && (
        <div id="faqEmpty" className="faq-empty" style={{ display: "block" }}>
          <strong>{t("help.noResults")}</strong>
          <p>{t("help.tryOther")}</p>
        </div>
      )}

      {visible.map((cat, i) => (
        <div className="faq-category" data-faq-category key={i}>
          <h2>{cat.title}</h2>
          <div className="faq-list">
            {cat.entries.map((entry, j) => (
              <FaqItem key={j} {...entry} />
            ))}
          </div>
          {cat.email && (
            <p>
              {t("help.moreQuestions")}{" "}
              <a
                href="https://dwik.art/index.html#/contact"
                target="_self"
              >
                {t("help.contactUs")}
              </a>
            </p>          
          )}
        </div>
      ))}
    </section>
  );
}
